import React, { useState, useEffect, useCallback } from 'react';
import { Rocket, Check, SkipForward, ChevronRight, Loader2, Globe, Users, Shield, Cpu, Building2, Database } from 'lucide-react';
import { API } from '../../App';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';
const btnPrimary = 'px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded hover:bg-[#E63E00] transition-colors';
const btnSecondary = 'px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm rounded hover:border-[#FF4D00] hover:text-[#F5F5F5] transition-colors';

const STEP_ICONS = {
  domain: Globe,
  members: Users,
  users: Users,
  sso: Shield,
  models: Cpu,
  security: Shield,
  departments: Building2,
  governance: Database,
};

export default function OnboardingWizard({ workspaceId }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}/onboarding`);
      // Steps now come pre-merged with status from backend
      setSteps(r.data.steps || []);
    } catch { setSteps([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // #538: Map step IDs to workspace admin tab names for navigation
  const STEP_TAB_MAP = {
    domain: 'domains',
    sso: 'security',
    security: 'security',
    departments: 'departments',
    models: 'governance',
    users: 'users',
  };

  const [confirmStep, setConfirmStep] = useState(null);

  const completeStep = async (stepId) => {
    setCompleting(stepId);
    try {
      await API.post(`/api/workspaces/${workspaceId}/onboarding/${stepId}`);
      fetchStatus();
      setConfirmStep(null);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to complete step'); }
    setCompleting(null);
  };

  const skipStep = async (stepId) => {
    setCompleting(stepId);
    try {
      await API.post(`/api/workspaces/${workspaceId}/onboarding/skip/${stepId}`);
      fetchStatus();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to skip step'); }
    setCompleting(null);
  };

  const totalSteps = steps.length;
  const completedSteps = steps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
  const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  if (loading) return <div className="text-center py-10 text-[#A0A0A0]">Loading...</div>;

  if (steps.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold font-['Barlow_Condensed'] text-[#F5F1E8]">Enterprise Onboarding</h2>
        <div className={`${card} text-center`}>
          <Rocket className="w-8 h-8 mx-auto mb-2 text-[#FF4D00] opacity-50" />
          <p className="text-sm text-[#A0A0A0]">No onboarding steps configured for this workspace</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-['Barlow_Condensed'] text-[#F5F1E8]">Enterprise Onboarding</h2>
        <span className="text-xs font-mono text-[#A0A0A0]">{completedSteps}/{totalSteps} complete</span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-[#0F0F0F] rounded-full overflow-hidden">
        <div className="h-full bg-[#FF4D00] transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
      </div>

      {progress === 100 && (
        <div className={`${card} border-[#00D4FF]/30 text-center`}>
          <Check className="w-8 h-8 mx-auto mb-2 text-[#00D4FF]" />
          <p className="text-sm text-[#F5F1E8] font-bold">Onboarding Complete!</p>
          <p className="text-xs text-[#A0A0A0] mt-1">Your enterprise workspace is fully configured</p>
        </div>
      )}

      <div className="space-y-2">
        {steps.map((step, i) => {
          const StepIcon = STEP_ICONS[step.id] || STEP_ICONS[step.type] || ChevronRight;
          const isComplete = step.status === 'completed' || step.status === 'skipped';
          const isCurrent = !isComplete && (i === 0 || steps[i - 1]?.status === 'completed' || steps[i - 1]?.status === 'skipped');

          return (
            <div key={step.id} className={`${card} ${isCurrent ? 'border-[#FF4D00]/50' : ''} ${isComplete ? 'opacity-60' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isComplete ? 'bg-[#00D4FF]/20' : isCurrent ? 'bg-[#FF4D00]/20' : 'bg-[#2A2A2A]'
                  }`}>
                    {isComplete ? (
                      <Check className="w-4 h-4 text-[#00D4FF]" />
                    ) : (
                      <StepIcon className={`w-4 h-4 ${isCurrent ? 'text-[#FF4D00]' : 'text-[#A0A0A0]'}`} />
                    )}
                  </div>
                  <div>
                    <span className={`text-sm font-mono ${isComplete ? 'text-[#A0A0A0] line-through' : 'text-[#F5F1E8]'}`}>
                      {step.title || step.name || step.id}
                    </span>
                    {step.description && <p className="text-xs text-[#A0A0A0]">{step.description}</p>}
                    {step.status === 'skipped' && <span className="text-[10px] font-mono text-[#F2A900]">SKIPPED</span>}
                  </div>
                </div>
                {!isComplete && (
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmStep(step.id)} disabled={completing === step.id} className={btnPrimary}>
                      {completing === step.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete'}
                    </button>
                    <button onClick={() => skipStep(step.id)} disabled={completing === step.id} className={btnSecondary}>
                      <SkipForward className="w-3.5 h-3.5 inline mr-1" />Skip
                    </button>
                  </div>
                )}
                {/* #538: Confirmation dialog before marking complete */}
                {confirmStep === step.id && (
                  <div className="mt-3 p-3 bg-[#0F0F0F] border border-[#FF4D00]/30 rounded-lg">
                    <p className="text-xs text-[#F5F1E8] font-mono mb-2">
                      Have you configured <strong>{step.title || step.name}</strong>?
                    </p>
                    <div className="flex gap-2">
                      {STEP_TAB_MAP[step.id] && (
                        <button
                          onClick={() => { setConfirmStep(null); /* Parent handles tab switch via onNavigate prop or similar */ }}
                          className={btnSecondary + ' !text-[10px]'}
                        >
                          Go to {step.title || step.name} settings →
                        </button>
                      )}
                      <button onClick={() => completeStep(step.id)} disabled={completing === step.id} className={btnPrimary + ' !text-[10px]'}>
                        {completing === step.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Mark Complete'}
                      </button>
                      <button onClick={() => setConfirmStep(null)} className={btnSecondary + ' !text-[10px]'}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
